'use client'; // This tells Next.js that this component is a Client Component

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import { GamesOutlined } from '@mui/icons-material';
import { MoneyRounded } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { PaymentsOutlined } from '@mui/icons-material';
import { ShareLocation } from '@mui/icons-material'
import Toolbar from '@mui/material/Toolbar';
import { useEffect, useState } from 'react';
import { Collapse, useMediaQuery, useTheme } from '@mui/material';
import { GroupOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLogout } from '@/hooks/api/useAuth';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Header } from './layout/Header';
import AtmIcon from '@mui/icons-material/Atm';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AppRoutes from './AppRoutes';
import { ExpandMore } from '@mui/icons-material';
import { useAuthData } from '@/hooks/getAuthData';
import { AdfScanner } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import EventIcon from '@mui/icons-material/Event';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HistoryIcon from '@mui/icons-material/History';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ViewListIcon from '@mui/icons-material/ViewList';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LabelIcon from '@mui/icons-material/Label';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { isSouthernMintContext, isTomsVendingContext, isInventoryAuditorContext } from '@/constants/business-units';
import { isReportOnlyUser } from '@/utils/roles';
import { APP_BAR_HEIGHT_PX } from '@/constants/branding';

const drawerWidth = 240;
const drawerWidthCollapsed = 64;

function AppAuthenticated(props: any) {

	const { t } = useTranslation();

	const navigate = useNavigate();
	const location = useLocation(); // Gets the full path with query parameters

	const [fullRoute, setFullRoute] = useState('');

	useEffect(() => {
		setFullRoute(location.pathname + location.search); // Combine pathname and query parameters
	}, [location]);

	const theme = useTheme();
	const matchesXSorSM = useMediaQuery(theme.breakpoints.down('md')); // Collapse on screens below 960px

	const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Desktop sidebar collapse state

	const logoutMutation = useLogout();
	const { window } = props;
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};
	const handleDrawerClose = () => {
		if (matchesXSorSM) {
			setMobileOpen(false);
		}
	};

	const { user, roles, userRoles } = useAuthData();

	const hasTechnicianRole = roles.some((x) => x.name === 'Technician');
	const hasAdminRole = roles.some(
		(x) => x.name === 'Admin' || x.name === 'SystemAdmin',
	);
	const reportOnlyUser = isReportOnlyUser(roles);

	const showSouthernMintNav = isSouthernMintContext(user);
	const showTomsVendingNav = isTomsVendingContext(user);
	const showInventoryAuditorNav = isInventoryAuditorContext(user);

	const iconStyle = {}
	const subItemStyle = { cursor: 'pointer', paddingLeft: 73 }

	const collapseMenuItems = [
		{ key: 'goa', icon: <GamesOutlined />, label: 'goa', assetLink: '/app/goas' },
		{ key: 'trk', icon: <MoneyRounded />, label: 'trk' },
		{ key: 'atm', icon: <AtmIcon />, label: 'atm' },
		{ key: 'ctm', icon: <AdfScanner />, label: 'ctm' }, // Example: Replace with a relevant icon
	];

	const CollapsibleMenuItem = ({ item, navigate, handleDrawerClose, iconStyle, assetLink, sidebarCollapsed, matchesXSorSM }: any) => {
		const [open, setOpen] = useState(false);

		// Don't show collapsible menu when sidebar is collapsed (desktop only)
		if (!matchesXSorSM && sidebarCollapsed) {
			return null;
		}

		return (
			<>
				<ListItem component="li" key={item.key} style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
					<ListItemIcon>{item.icon}</ListItemIcon>
					<ListItemText primary={sidebarText(t(item.label))} />
					<ExpandMore className={open ? 'rotate-180' : ''} />
				</ListItem>
				<Collapse in={open} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						<ListItem 
							component="li" 
							sx={{ 
								pl: 4,
								py: 0.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.04)'
								}
							}}
							onClick={() => {
								handleDrawerClose();
								navigate(assetLink ? assetLink : `/app/${item.key}/assets`);
							}}
						>
							<ListItemText 
								primary={t('assets')} 
								primaryTypographyProps={{
									fontSize: '0.875rem',
									fontWeight: 500
								}}
							/>
						</ListItem>
						<ListItem 
							component="li" 
							sx={{ 
								pl: 4,
								py: 0.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.04)'
								}
							}}
							onClick={() => {
								handleDrawerClose();
								navigate(`/app/${item.key}/events`);
							}}
						>
							<ListItemText 
								primary={t('events')} 
								primaryTypographyProps={{
									fontSize: '0.875rem',
									fontWeight: 500
								}}
							/>
						</ListItem>
					</List>
				</Collapse>
			</>
		);
	};

	const sidebarText = (text: string) => {
		return <b>{text}</b>
	};

	// Highlight color for selected nav item (semi-transparent primary)
	const highlightBg = theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.16)' : 'rgba(25, 118, 210, 0.12)';
	const selectedBg = (path: string) => {
		const match = location.pathname === path || (path !== '/app' && location.pathname.startsWith(path + '/'));
		return match ? { backgroundColor: highlightBg } : {};
	};
	// Assets header: highlighted when on /app/assets OR when any asset sub-tab is selected
	const assetsSectionSelected = location.pathname === '/app/assets' ||
		collapseMenuItems.some((item) => {
			const p = item.assetLink || `/app/${item.key}/assets`;
			return location.pathname === p || location.pathname.startsWith(p + '/');
		});
	// Events header: highlighted when on /app/events OR when any events sub-tab is selected
	const eventsSectionSelected = location.pathname === '/app/events' ||
		collapseMenuItems.some((item) => {
			const p = `/app/${item.key}/events`;
			return location.pathname === p || location.pathname.startsWith(p + '/');
		});

	const [assetsOpen, setAssetsOpen] = useState(false);
	const [eventsOpen, setEventsOpen] = useState(false);

	// Keep Assets/Events expanded when the current route is in that section (avoids auto-close on re-render/nav)
	useEffect(() => {
		if (assetsSectionSelected) setAssetsOpen(true);
	}, [location.pathname]);
	useEffect(() => {
		if (eventsSectionSelected) setEventsOpen(true);
	}, [location.pathname]);

	const currentDrawerWidth = !matchesXSorSM && sidebarCollapsed ? drawerWidthCollapsed : drawerWidth;

	const drawer = (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<List>
				<ListItem component={Link} to="/app/dashboard" onClick={handleDrawerClose} key={'dashboard'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/dashboard'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<DashboardIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('dashboard'))} />}
				</ListItem>

				{showSouthernMintNav && (
				<ListItem component={Link} to="/app/locations" onClick={handleDrawerClose} key={'locations'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/locations'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LocationOnIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('locations'))} />}
				</ListItem>
				)}

				{showSouthernMintNav && (
				<ListItem component={Link} to="/app/location-service" onClick={handleDrawerClose} key={'ach'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/location-service'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<ShareLocation style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Service')} />}
				</ListItem>
				)}

				{showInventoryAuditorNav && (
				<ListItem component={Link} to="/app/inventory-auditor/reports" onClick={handleDrawerClose} key={'ia-reports'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/inventory-auditor/reports'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<AssessmentIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Reports')} />}
				</ListItem>
				)}

				{showInventoryAuditorNav && (
				<ListItem component={Link} to="/app/inventory-auditor/locations" onClick={handleDrawerClose} key={'ia-locations'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/inventory-auditor/locations'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LocationOnIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Locations')} />}
				</ListItem>
				)}

				{showInventoryAuditorNav && (
				<ListItem component={Link} to="/app/inventory-auditor/site-visit" onClick={handleDrawerClose} key={'ia-site-visit'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/inventory-auditor/site-visit'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<FactCheckIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Store count')} />}
				</ListItem>
				)}

				{showInventoryAuditorNav && (
				<ListItem component={Link} to="/app/inventory-auditor/catalog" onClick={handleDrawerClose} key={'ia-catalog'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/inventory-auditor/catalog'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<ViewListIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Item catalog')} />}
				</ListItem>
				)}

				{showInventoryAuditorNav && (
				<ListItem component={Link} to="/app/inventory-auditor/purchasing/purchase-orders" onClick={handleDrawerClose} key={'ia-purchasing'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/inventory-auditor/purchasing'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<ShoppingCartIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Purchasing')} />}
				</ListItem>
				)}

				{showTomsVendingNav && (
				<ListItem component={Link} to="/app/toms-vending/reports" onClick={handleDrawerClose} key={'toms-vending-reports'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(location.pathname === '/app/toms-vending/reports' ||
						location.pathname.startsWith('/app/toms-vending/reports/') ||
						location.pathname.startsWith('/app/toms-vending/site-visits/')
							? { backgroundColor: highlightBg }
							: {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<AssessmentIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Reports')} />}
				</ListItem>
				)}

				{showTomsVendingNav && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/locations" onClick={handleDrawerClose} key={'toms-vending-locations'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/locations'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LocationOnIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('locations'))} />}
				</ListItem>
				)}

				{showTomsVendingNav && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/purchasing/purchase-orders" onClick={handleDrawerClose} key={'tv-purchasing'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/purchasing/purchase-orders'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<ShoppingCartIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Purchasing')} />}
				</ListItem>
				)}

				{showTomsVendingNav && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/purchasing/vendors" onClick={handleDrawerClose} key={'tv-vendors'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/purchasing/vendors'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LocalShippingIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Vendors')} />}
				</ListItem>
				)}

				{showTomsVendingNav && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/vending-machines" onClick={handleDrawerClose} key={'toms-vending-machines'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/vending-machines'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<StorefrontIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Vending machines')} />}
				</ListItem>
				)}

				{showTomsVendingNav && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/inventory/movements" onClick={handleDrawerClose} key={'toms-vending-inventory-movements'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/inventory/movements'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<HistoryIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Stock movements')} />}
				</ListItem>
				)}

				{showTomsVendingNav && hasAdminRole && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/inventory/reject-summary" onClick={handleDrawerClose} key={'toms-vending-reject-summary'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/toms-vending/inventory/reject-summary'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<HistoryIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Vending reject totals')} />}
				</ListItem>
				)}

				{showTomsVendingNav && hasAdminRole && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/inventory" onClick={handleDrawerClose} key={'toms-vending-inventory'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(location.pathname === '/app/toms-vending/inventory' ||
						location.pathname === '/app/toms-vending/inventory/quick-edit' ||
						location.pathname === '/app/toms-vending/inventory/brands' ||
						location.pathname === '/app/toms-vending/inventory/categories'
							? { backgroundColor: highlightBg }
							: {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<Inventory2Icon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Catalog')} />}
				</ListItem>
				)}

				{showTomsVendingNav && hasAdminRole && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/inventory/brands" onClick={handleDrawerClose} key={'toms-vending-brands'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(location.pathname === '/app/toms-vending/inventory/brands' ? { backgroundColor: highlightBg } : {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LabelIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Brands')} />}
				</ListItem>
				)}

				{showTomsVendingNav && hasAdminRole && !reportOnlyUser && (
				<ListItem component={Link} to="/app/toms-vending/inventory/categories" onClick={handleDrawerClose} key={'toms-vending-categories'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(location.pathname === '/app/toms-vending/inventory/categories' ? { backgroundColor: highlightBg } : {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<CategoryIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Categories')} />}
				</ListItem>
				)}

				{hasAdminRole && <ListItem component={Link} to="/app/business-units" onClick={handleDrawerClose} key={'business-units'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/business-units'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<BusinessIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText('Business Units')} />}
				</ListItem>}

				{hasAdminRole && <ListItem component={Link} to="/app/users" onClick={handleDrawerClose} key={'users'}
					sx={{
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...selectedBg('/app/users'),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<GroupOutlined style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('Users'))} />}
				</ListItem>}

				{showSouthernMintNav && (
				<>
				{/* Assets Section */}
				<ListItem 
					component={Link}
					to="/app/assets"
					onClick={handleDrawerClose}
					key={'assets-header'}
					sx={{ 
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(assetsSectionSelected ? { backgroundColor: highlightBg } : {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<PointOfSaleIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('Assets'))} />}
					{(!matchesXSorSM && sidebarCollapsed) ? null : (
						<ExpandMore 
							className={assetsOpen ? 'rotate-180' : ''} 
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setAssetsOpen(!assetsOpen);
							}}
							sx={{ cursor: 'pointer' }}
						/>
					)}
				</ListItem>
				{(!matchesXSorSM && sidebarCollapsed) ? null : (
					<Collapse in={assetsOpen} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{collapseMenuItems.map((item) => {
									const assetPath = item.assetLink || `/app/${item.key}/assets`;
									return (
									<ListItem 
										key={`${item.key}-assets`}
										component={Link}
										to={assetPath}
										onClick={handleDrawerClose}
										sx={{ 
											pl: 4,
											py: 0.5,
											cursor: 'pointer',
											...selectedBg(assetPath),
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)'
											},
											textDecoration: 'none',
											color: 'inherit',
										}}
									>
										<ListItemText 
											primary={t(item.label)} 
											primaryTypographyProps={{
												fontSize: '0.875rem',
												fontWeight: 500
											}}
										/>
									</ListItem>
									);
								})}
							</List>
					</Collapse>
				)}

				{/* Events Section */}
				<ListItem 
					component={Link}
					to="/app/events"
					onClick={handleDrawerClose}
					key={'events-header'}
					sx={{ 
						cursor: 'pointer',
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
						...(eventsSectionSelected ? { backgroundColor: highlightBg } : {}),
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<EventIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('Events'))} />}
					{(!matchesXSorSM && sidebarCollapsed) ? null : (
						<ExpandMore 
							className={eventsOpen ? 'rotate-180' : ''} 
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setEventsOpen(!eventsOpen);
							}}
							sx={{ cursor: 'pointer' }}
						/>
					)}
				</ListItem>
				{(!matchesXSorSM && sidebarCollapsed) ? null : (
					<Collapse in={eventsOpen} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{collapseMenuItems.map((item) => {
									const eventsPath = `/app/${item.key}/events`;
									return (
									<ListItem 
										key={`${item.key}-events`}
										component={Link}
										to={eventsPath}
										onClick={handleDrawerClose}
										sx={{ 
											pl: 4,
											py: 0.5,
											cursor: 'pointer',
											...selectedBg(eventsPath),
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.04)'
											},
											textDecoration: 'none',
											color: 'inherit',
										}}
									>
										<ListItemText 
											primary={t(item.label)} 
											primaryTypographyProps={{
												fontSize: '0.875rem',
												fontWeight: 500
											}}
										/>
									</ListItem>
									);
								})}
							</List>
					</Collapse>
				)}
				</>
				)}

			</List>
			<Divider />
			<List>
				<ListItem style={{ cursor: 'pointer' }} component="li" key={'logout'}
					onClick={async (): Promise<void> => {
						handleDrawerClose();
						logoutMutation.mutate();
					}}
					sx={{
						justifyContent: (!matchesXSorSM && sidebarCollapsed) ? 'center' : 'flex-start',
					}}
				>
					<ListItemIcon sx={{ minWidth: (!matchesXSorSM && sidebarCollapsed) ? 0 : 56, justifyContent: 'center' }}>
						<LogoutIcon style={iconStyle} />
					</ListItemIcon>
					{(!matchesXSorSM && sidebarCollapsed) ? null : <ListItemText primary={sidebarText(t('Log Out'))} />}
				</ListItem>
			</List>
			{/* Collapse/Expand Toggle Button - Desktop Only, at bottom */}
			{!matchesXSorSM && (
				<Box sx={{ 
					display: 'flex', 
					justifyContent: 'flex-end', 
					p: 1,
					mt: 'auto',
					borderTop: '1px solid rgba(0, 0, 0, 0.12)'
				}}>
					<IconButton
						onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
						size="small"
						sx={{
							transition: 'transform 0.2s',
						}}
					>
						{sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
					</IconButton>
				</Box>
			)}
		</div>
	);
	const container = window !== undefined ? () => window().document.body : undefined;

	// if (!userDataRefreshed) {
	// 	return null;
	// }

	return (
		<div style={{ width: '100vw', backgroundColor: theme.palette.background.paper, color: theme.palette.primary.light }}>
			<Box sx={{ display: 'flex' }}>
				{/* Hamburger menu for mobile */}
				{matchesXSorSM && !mobileOpen && (
					<IconButton
						color="inherit"
						aria-label="open drawer"
						edge="start"
						onClick={handleDrawerToggle}
						sx={{
							position: 'fixed',
							top: 8,
							left: 16,
							zIndex: theme.zIndex.drawer + 2, // above the drawer
							background: theme.palette.background.paper,
						}}
					>
						<MenuIcon />
					</IconButton>
				)}

				<Header inset={!matchesXSorSM && !sidebarCollapsed} drawerWidth={currentDrawerWidth} />

				<Box
					component="nav"
					sx={{ width: matchesXSorSM && !mobileOpen ? 0 : { sm: matchesXSorSM ? drawerWidth : currentDrawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.3s ease' }}
					aria-label="mailbox folders"
				>
					<Drawer
						slotProps={{
							paper: {
								sx: matchesXSorSM
									? {
										backgroundColor: theme.palette.background.paper,
										color: theme.palette.primary.light,
									}
									: {
										backgroundColor: theme.palette.background.paper,
										color: theme.palette.primary.light,
										top: `${APP_BAR_HEIGHT_PX}px`,
										height: `calc(100% - ${APP_BAR_HEIGHT_PX}px)`,
										transition: 'width 0.3s ease',
									},
							},
						}}
						container={container}
						variant={matchesXSorSM ? "temporary" : "permanent"}
						open={matchesXSorSM ? mobileOpen : true}
						onClose={handleDrawerClose}
						ModalProps={{
							keepMounted: true,
						}}
						sx={{
							display: matchesXSorSM && !mobileOpen ? 'none' : 'block',
							'& .MuiDrawer-paper': {
								boxSizing: 'border-box',
								width: matchesXSorSM && !mobileOpen ? 0 : (matchesXSorSM ? drawerWidth : currentDrawerWidth),
								transition: 'width 0.3s ease',
								overflowX: 'hidden',
							},
						}}
					>
						{drawer}
					</Drawer>
				</Box>

				<Box
					component="main"
					sx={{
						flexGrow: 1,
						p: { xs: 1, sm: 3 },
						marginTop: 8,
						width: matchesXSorSM ? '100%' : { sm: `calc(100% - ${currentDrawerWidth}px)` },
						transition: 'width 0.3s ease',
					}}
				>
					{/* {props.children} */}
					<AppRoutes />
				</Box>
			</Box>
		</div>

	)

}

export default AppAuthenticated